/**
 * GeoRecheckOverlay — the customer-side FRESH location re-capture for an officer GEO prompt
 * (the SPEC-06 §5.1 `geo-recheck` PROMPTED primitive — "Location re-capture (GPS)" — on the
 * S13-8/9 prompt plane).
 *
 * SCOPE, precisely (landed-shape note, 20-Aug round): the #13 IP-drift re-check itself landed on
 * a DEDICATED passive wire (be `0a2140e`: ipdrift:pending → ipdrift:capture; droom answers
 * silently — media/useIpdriftRecheck.ts). THIS overlay is the GPS half: when the be's S13-8 slice
 * dispatches a `geo-recheck` prompt on the channel, the customer is asked — transparently — to
 * re-share their LOCATION, and the answer is a fresh `POST /sessions/{id}/geo`
 * (`recapture: true`). The server derives the current source IP from that authed request under
 * its XFF/trusted-proxy discipline — the client never sends an IP, never a verdict. Until the be
 * dispatches that prompt key this overlay is forward-contract code (the requestedFacing posture:
 * a droom running ahead of the backend, honestly inert).
 *
 * Honesty rules (the useGeoEvidence posture, applied to the prompted moment):
 *   • TRANSPARENCY FIRST: the overlay opens by saying WHO asked (your verifier) and WHAT FOR (a
 *     fresh location/network check); ack-on-show tells the server the customer has seen it before
 *     anything else happens. The browser permission prompt fires only from the explicit button —
 *     never as an ambush.
 *   • A denial/timeout/unsupported device STILL POSTS — the honest missing state (permissionState
 *     says why, no coordinates). Refusing to post because there is no GPS fix would discard the
 *     fresh IP observation too. The done copy says plainly which of the two happened.
 *   • Declining is SPOKEN, exactly like a photo prompt: the customer tells the verifier, the
 *     verifier withdraws, and the poll closes the overlay (withdrawal/expiry are recorded outcomes
 *     server-side — nothing vanishes, nothing is captured silently).
 *   • Nothing is fabricated client-side, and no coordinates are ever logged.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/runtime';
import { ackPrompt, type CapturePrompt } from '@/api/prompts';
import { postGeoEvidence, type GeoCaptureBody, type GeoPermissionState } from '@/api/geo';
import { Button } from './Button';
import { Icon } from './Icon';

/** Mirrors useGeoEvidence's fix window — one platform-wide patience for a GPS fix. */
const FIX_TIMEOUT_MS = 15_000;
const DONE_AUTORETURN_MS = 1_800;

/**
 * The prompted geo primitive key this overlay answers: `geo-recheck`, the be catalog's PROMPTED
 * geo primitive (SPEC-06 §5.1). Deliberately NOT `ipdrift-recheck` — that key landed as a
 * dedicated PASSIVE wire (be `0a2140e`), never a prompt; answering it here would put a GPS
 * ceremony in front of a zero-interaction network check.
 */
export function isGeoRecheckPromptKey(primitiveKey: string): boolean {
  return primitiveKey === 'geo-recheck';
}

type Phase =
  | 'explain' // who asked + what for; the browser prompt has NOT fired yet
  | 'requesting' // browser permission / fix acquisition in flight
  | 'posting' // fresh capture (or honest missing state) being recorded
  | 'done' // recorded with a GPS fix
  | 'done-missing' // recorded WITHOUT a fix (denied/timeout/unsupported) — network leg still fresh
  | 'post-failed'; // the record POST failed; retry / return to the call

export function GeoRecheckOverlay({
  sessionId,
  prompt,
  onBusyChange,
  onDone,
}: {
  sessionId: string;
  prompt: CapturePrompt;
  /** Safe-point signal to the poll: true while the capture/record ceremony is in flight. */
  onBusyChange: (busy: boolean) => void;
  onDone: (outcome: 'completed' | 'withdrawn') => void;
}): JSX.Element {
  const { t } = useT();
  const [phase, setPhase] = useState<Phase>('explain');
  const disposed = useRef(false);
  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  // Ack-on-show (idempotent, non-blocking): the customer has SEEN who asked and what for before
  // the ceremony does anything — the same REG transparency floor as the photo prompts.
  useEffect(() => {
    ackPrompt(sessionId, prompt.id).catch(() => {});
    // once per prompt id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, prompt.id]);

  const busy = phase === 'requesting' || phase === 'posting';
  useEffect(() => {
    onBusyChange(busy);
    return () => onBusyChange(false);
  }, [busy, onBusyChange]);

  // Auto-return after the done beat — the officer sees the result arrive on their own panel.
  useEffect(() => {
    if (phase !== 'done' && phase !== 'done-missing') return undefined;
    const timer = setTimeout(() => {
      if (!disposed.current) onDone('completed');
    }, DONE_AUTORETURN_MS);
    return () => clearTimeout(timer);
  }, [phase, onDone]);

  const record = useCallback(
    async (body: GeoCaptureBody, hadFix: boolean) => {
      setPhase('posting');
      try {
        // recapture: true ALWAYS — this session already holds join/call-screen rows; the re-check
        // is an explicit append on the same ladder, never a first-capture 409 dance.
        await postGeoEvidence(sessionId, { ...body, recapture: true });
        if (disposed.current) return;
        setPhase(hadFix ? 'done' : 'done-missing');
      } catch {
        if (disposed.current) return;
        setPhase('post-failed');
      }
    },
    [sessionId],
  );

  const share = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      void record({ permissionState: 'UNSUPPORTED' }, false);
      return;
    }
    setPhase('requesting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (disposed.current) return;
        const rawMocked = (pos.coords as GeolocationCoordinates & { mocked?: unknown }).mocked;
        void record(
          {
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            accuracyM: pos.coords.accuracy,
            capturedAtMs: pos.timestamp,
            permissionState: 'GRANTED',
            clientHints: { mocked: typeof rawMocked === 'boolean' ? rawMocked : null },
          },
          true,
        );
      },
      (err) => {
        if (disposed.current) return;
        let state: GeoPermissionState = 'ERROR';
        if (err.code === err.PERMISSION_DENIED) state = 'DENIED';
        else if (err.code === err.TIMEOUT) state = 'TIMEOUT';
        // The honest missing state still posts — that authed POST carries the fresh network
        // observation, which is the half of the re-check GPS cannot veto.
        void record({ permissionState: state }, false);
      },
      { enableHighAccuracy: true, timeout: FIX_TIMEOUT_MS, maximumAge: 0 },
    );
  }, [record]);

  return (
    <div className="gcap" role="dialog" aria-modal="true" aria-label={t('droom.georecheck.title')}>
      {/* Who asked + what for — shown BEFORE anything is captured (REG floor). */}
      <div className="gcap-head">
        <span className="gcap-badge">
          <Icon name="mappin" size={14} />
          {t('droom.gcap.badge')}
        </span>
        <div className="gcap-title">{t('droom.georecheck.title')}</div>
        <p className="gcap-why">{t('droom.georecheck.asked')}</p>
      </div>

      {phase === 'explain' ? (
        <div className="gcap-actions">
          <p className="gcap-why">{t('droom.georecheck.privacy')}</p>
          <Button onClick={share}>{t('droom.georecheck.share')}</Button>
          <p className="gcap-decline">{t('droom.gcap.decline')}</p>
        </div>
      ) : null}

      {phase === 'requesting' ? (
        <p className="gcap-why" role="status" aria-live="polite">
          {t('droom.georecheck.requesting')}
        </p>
      ) : null}
      {phase === 'posting' ? (
        <p className="gcap-why" role="status" aria-live="polite">
          {t('droom.georecheck.posting')}
        </p>
      ) : null}

      {phase === 'done' || phase === 'done-missing' ? (
        <div className="gcap-done" role="status" aria-live="polite" style={{ position: 'static', flex: 1 }}>
          <span className="gcap-done-badge">
            <Icon name="check" size={26} />
          </span>
          <div className="gcap-done-title">
            {t(phase === 'done' ? 'droom.georecheck.done.title' : 'droom.georecheck.done.missing.title')}
          </div>
          <div className="gcap-done-sub">
            {t(phase === 'done' ? 'droom.georecheck.done.sub' : 'droom.georecheck.done.missing.sub')}
          </div>
        </div>
      ) : null}

      {phase === 'post-failed' ? (
        <div className="gcap-actions" role="alert">
          <p className="gcap-why">{t('droom.georecheck.failed')}</p>
          <Button onClick={share}>{t('droom.action.tryAgain')}</Button>
          <Button variant="ghost" onClick={() => onDone('withdrawn')} type="button">
            {t('droom.gcap.back')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
